<script setup>
import { RouterView }   from 'vue-router';
import { HCol, HAlert } from '@pixul/hephaestus';
import { useUserStore } from '@/store/user';
import Navbar           from '@/components/Navbar/Navbar.vue';
import FooterVersion    from '@/components/FooterVersion/FooterVersion.vue';

const store = useUserStore();
</script>

<template>
  <HCol class='wrapper'>
    <Navbar v-if='store.token' />

    <HCol
      ref='container'
      :class='containerClasses'
    >
      <router-view v-slot='{ Component, route }'>
        <Transition
          mode='out-in'
          name='fade'
        >
          <HCol
            :key='route.name'
            class='flex-1'
          >
            <component :is='Component' />
          </HCol>
        </Transition>
      </router-view>
    </HCol>

    <HAlert
      v-if='alert.show'
      :dismissible='alert.dismissible'
      :fixed='alert.fixed'
      :position='alert.position'
      :timeout='alert.timeout'
      :type='alert.type'
      @dismiss='dismissAlert'
    >
      <template #default>
        <template v-if='alert.messages'>
          <p
            v-for='message in alert.messages'
            :key='message'
          >
            {{ message }}
          </p>
        </template>
        <p v-else>
          {{ $t(`main.messages.${ alert.action }`, { model : alert.model }) }}
        </p>
      </template>
    </HAlert>
    <FooterVersion />
  </HCol>
</template>

<script>
import { computed }          from 'vue';
import { mapStores }         from 'pinia';
import Service               from '@/api/service';
import { useSaleTypesStore } from '@/store/saleTypes';
import { useBookmarkStore }  from '@/store/bookmark.js';

export default {
  name : 'App',

  provide() {

    return {
      containerElement : computed(() => this.containerElement),
      displayAlert     : this.displayAlert,
      dismissAlert     : this.dismissAlert
    };
  },

  data() {

    return {
      alert            : { show : false },
      intervalHandle   : null,
      containerElement : null
    };
  },

  computed : {
    ...mapStores(useUserStore, useSaleTypesStore, useBookmarkStore),

    containerClasses() {

      const classes = ['container', 'flex-1'];

      if (this.isMaxMedium) {

        classes.push('container--mobile');
      }

      if (this.$route.name?.includes('admin')) {

        classes.push('container--admin');
      }

      return classes;
    }
  },

  watch : {
    'userStore.user' : {
      async handler() {

        if (this.userStore.user?.id && this.intervalHandle === null) {

          await this.startInterval();
        } else if (!this.userStore.user?.id && this.intervalHandle) {

          clearInterval(this.intervalHandle);
        }
      }
    },

    'userStore.user.id' : {
      handler() {

        this.setupBookmarks();
      },

      deep : true
    },

    'userStore.showNavigationAlert' : {
      handler() {

        this.showNavigationAlert();
      }
    }
  },

  async created() {

    if (this.userStore.user?.id && this.intervalHandle === null) {

      await this.startInterval();
    } else if (!this.userStore.user?.id && this.intervalHandle) {

      clearInterval(this.intervalHandle);
    }
  },

  mounted() {

    this.containerElement = this.$refs['container'];
  },

  methods : {
    async startInterval() {

      await this.setupGlobalInfos();

      this.intervalHandle = setInterval(() => {

        if (!this.userStore.user?.id) {

          if (this.$route.name !== 'login') {

            this.userStore.logout();
          }

          clearInterval(this.intervalHandle);
          return;
        }

        this.userStore.checkLoginExpDate();
      }, 1000);
    },

    async setupGlobalInfos() {

      const response = await Service.getSaleTypes(this.userStore.tenant, { perPage : 1000 });
      this.saleTypesStore.setSaleTypes(response);
      this.setupBookmarks();
    },

    async setupBookmarks() {

      if (this.userStore.tenant && this.userStore.user.id) {

        try {

          const bookmarks = await Service.getBookmarks(this.userStore.tenant, this.userStore.user.id);
          this.bookmarkStore.setBookmarks(bookmarks.filter(bookmark => bookmark.metadata.service === 'commissions'));
        } catch {
          // empty
        }

        return;
      }

      this.bookmarkStore.setBookmarks({});
    },

    showNavigationAlert() {

      if (this.userStore.showNavigationAlert) {
        this.displayAlert({
          type     : 'warning',
          timeout  : 2000,
          messages : [
            this.$t('general.apiWarning')
          ]
        });
      }
    },

    displayAlert({
      type, action, model, messages = null, fixed = true, timeout = 10000, dismissible = true, position
    }) {

      this.alert = {
        show        : true,
        type        : type,
        action      : action,
        model       : model,
        messages    : messages,
        timeout     : timeout,
        fixed       : fixed,
        dismissible : dismissible,
        position    : position
      };
    },

    dismissAlert() {

      this.alert = { show : false };
    }
  }
};
</script>

<style rel="stylesheet/scss" lang="scss">
body {
  overflow-x: hidden;
}

#app {
  overflow: hidden;
}

a {
  text-decoration: none;
  color: var(--grey-600);
}

</style>

<style scoped>
.wrapper {
    width: 100vw;
    height: 100%;
    min-height: 100vh;
    background: var(--grey-200);
}

.container {
    padding: 20px 30px;
}

.container--mobile {
    padding: 18px 16px;
}

.container--admin {
    padding: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
